// ============================================================================
// EMPLOYEE IMAGE SERVICE - SIMPLE CLEAN VERSION
// File: frontend/src/services/employeeImage.service.ts
// ============================================================================

import { doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';

// 🎯 MINIMAL interface - only what we need for notifications
export interface EmployeeImageData {
  employeeId: string;
  name: string;
  imageUrl: string | null;
  initials: string;
}

// 🎯 CACHE interface
interface ImageCacheEntry {
  imageUrl: string | null;
  name: string;
  initials: string;
  timestamp: number;
}

class EmployeeImageService {
  private imageCache = new Map<string, ImageCacheEntry>();
  private loadingSet = new Set<string>();
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
  private readonly DEFAULT_AVATAR_SIZE = 44;

  /**
   * 🚀 MAIN METHOD: Get employee image data for notifications
   */
  async getEmployeeImageData(employeeId: string): Promise<EmployeeImageData> {
    try {
      // Check cache first
      const cached = this.getFromCache(employeeId);
      if (cached) {
        return {
          employeeId,
          name: cached.name,
          imageUrl: cached.imageUrl,
          initials: cached.initials
        };
      }

      // Prevent duplicate requests
      if (this.loadingSet.has(employeeId)) {
        // Wait a bit and check cache again
        await this.delay(100);
        const retryCache = this.getFromCache(employeeId);
        if (retryCache) {
          return {
            employeeId,
            name: retryCache.name,
            imageUrl: retryCache.imageUrl,
            initials: retryCache.initials
          };
        }
      }

      // Start loading
      this.loadingSet.add(employeeId);

      console.log('🖼️ Fetching employee image data:', employeeId);

      // Get employee document (only name and image fields)
      const employeeRef = doc(db, 'employees', employeeId);
      const employeeDoc = await getDoc(employeeRef);

      let name = 'Unknown Employee';
      let imageUrl: string | null = null;

      if (employeeDoc.exists()) {
        const data = employeeDoc.data();
        name = data.name || 'Unknown Employee';
        const imageReference = data.image;

        // 🔧 SIMPLE: Handle different image storage formats
        if (imageReference) {
          imageUrl = await this.processImageReference(imageReference, name);
        }
      }

      const initials = this.generateInitials(name);

      // Cache the result
      this.setCache(employeeId, {
        imageUrl,
        name,
        initials,
        timestamp: Date.now()
      });

      return {
        employeeId,
        name,
        imageUrl,
        initials
      };

    } catch (error) {
      console.error('❌ Error fetching employee image data:', error);
      
      // Return fallback data
      const fallbackName = 'Unknown Employee';
      const fallbackInitials = this.generateInitials(fallbackName);
      
      return {
        employeeId,
        name: fallbackName,
        imageUrl: null,
        initials: fallbackInitials
      };
    } finally {
      this.loadingSet.delete(employeeId);
    }
  }

  /**
   * 🔧 SIMPLE: Process different image reference formats
   */
  private async processImageReference(imageReference: string, employeeName: string): Promise<string | null> {
    try {
      console.log('🔍 Processing image for:', employeeName, 'Length:', imageReference?.length);

      // 1. Base64 image data (starts with / and contains base64 data)
      if (this.isBase64Image(imageReference)) {
        console.log('📸 Converting base64 image for:', employeeName);
        return this.convertBase64ToDataUrl(imageReference);
      }

      // 2. HTTP/HTTPS URL (already ready to use)
      if (imageReference.startsWith('http')) {
        console.log('🌐 Using HTTP image URL for:', employeeName);
        return imageReference;
      }

      // 3. Firebase Storage path (convert to download URL)
      if (this.isFirebaseStoragePath(imageReference)) {
        console.log('☁️ Converting Firebase Storage path for:', employeeName);
        const imageRef = ref(storage, imageReference);
        const downloadUrl = await getDownloadURL(imageRef);
        console.log('✅ Firebase Storage URL resolved for:', employeeName);
        return downloadUrl;
      }

      // 4. Unknown format
      console.warn('⚠️ Unknown image format for:', employeeName);
      return null;

    } catch (error) {
      console.error('❌ Error processing image reference for:', employeeName, error);
      return null;
    }
  }

  /**
   * 🔄 SIMPLE: Convert base64 to data URL
   */
  private convertBase64ToDataUrl(base64Data: string): string {
    try {
      console.log('🔧 Simple base64 conversion, length:', base64Data.length);
      
      // Very basic cleaning
      let cleanBase64 = base64Data.startsWith('/') ? base64Data.substring(1) : base64Data;
      
      // Remove only obvious problematic characters
      cleanBase64 = cleanBase64.replace(/[\r\n\t]/g, '');
      
      // Create data URL (let browser handle the rest)
      const dataUrl = `data:image/jpeg;base64,${cleanBase64}`;
      
      console.log('✅ Created data URL, length:', dataUrl.length);
      return dataUrl;
      
    } catch (error) {
      console.error('❌ Simple conversion failed:', error);
      return '';
    }
  }

  /**
   * 🔍 Check if image is base64 data
   */
  private isBase64Image(imageReference: string): boolean {
    try {
      return imageReference.startsWith('/') && imageReference.length > 1000;
    } catch (error) {
      return false;
    }
  }

  /**
   * 🔍 Check if path is Firebase Storage path
   */
  private isFirebaseStoragePath(imageReference: string): boolean {
    return !imageReference.startsWith('/') && 
           !imageReference.startsWith('http') && 
           !imageReference.startsWith('data:') &&
           imageReference.length > 5;
  }

  /**
   * 🎨 GENERATE avatar URL (real image or initials fallback)
   */
  getAvatarUrl(imageData: EmployeeImageData, size: number = this.DEFAULT_AVATAR_SIZE): string {
    // Use real image if available
    if (imageData.imageUrl) {
      return imageData.imageUrl;
    }

    // Fallback to initials avatar
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(imageData.name)}&background=14b8a6&color=fff&size=${size}&rounded=true&font-size=0.6`;
  }

  /**
   * 🚀 BATCH method: Get multiple employee images
   */
  async getMultipleEmployeeImages(employeeIds: string[]): Promise<Map<string, EmployeeImageData>> {
    const results = new Map<string, EmployeeImageData>();
    const uniqueIds = [...new Set(employeeIds)];

    // Process in parallel
    const promises = uniqueIds.map(async (employeeId) => {
      try {
        const imageData = await this.getEmployeeImageData(employeeId);
        results.set(employeeId, imageData);
      } catch (error) {
        console.error(`Error loading image for ${employeeId}:`, error);
        // Add fallback data
        results.set(employeeId, {
          employeeId,
          name: 'Unknown Employee',
          imageUrl: null,
          initials: 'UE'
        });
      }
    });

    await Promise.allSettled(promises);
    return results;
  }

  /**
   * 🧹 PRIVATE: Cache management
   */
  private getFromCache(employeeId: string): ImageCacheEntry | null {
    const cached = this.imageCache.get(employeeId);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      console.log('📋 Using cached image data for:', employeeId);
      return cached;
    }
    
    if (cached) {
      this.imageCache.delete(employeeId);
    }
    return null;
  }

  private setCache(employeeId: string, data: ImageCacheEntry): void {
    this.imageCache.set(employeeId, data);
    console.log('💾 Cached image data for:', employeeId);
  }

  private generateInitials(name: string): string {
    if (!name || name.trim() === '') return 'UE';
    
    return name
      .split(' ')
      .filter(part => part.length > 0)
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2); // Max 2 characters
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 🧹 UTILITY methods
   */
  clearCache(): void {
    this.imageCache.clear();
    this.loadingSet.clear();
    console.log('🗑️ Employee image cache cleared');
  }

  getCacheSize(): number {
    return this.imageCache.size;
  }

  /**
   * 🧪 TEST method: Check if service is working
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      const testRef = doc(db, 'employees', 'test');
      await getDoc(testRef);
      
      return {
        success: true,
        message: 'Employee image service connection successful'
      };
    } catch (error) {
      return {
        success: false,
        message: `Employee image service connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }
}

// 🚀 EXPORT singleton instance
export const employeeImageService = new EmployeeImageService();